import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Card, CardActions, CardText } from 'react-md';
import Formsy from 'formsy-react';
import { Redirect } from 'react-router-dom';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { withSnackbar } from 'notistack';
import { isEqual, get } from 'lodash-es';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import MyInput from '../../Components/MyInput/MyInput';
import { I18nMarkdown } from '../../Components/Markdown';
import * as authActions from '../../Services/Auth/actions';
import * as themeActions from '../../Services/Theme/actions';
import CONFIG from '../../Config';
import { showAlerts } from '../Common/utils';
import Footer from '../Login/Footer';
import Header from '../Login/Header';

import './ChangePassword.scss';

class ChangePassword extends Component {
  constructor(props) {
    super(props);
    this.state = {
      password: '',
      password2: '',
      canSubmit: false,
    };
  }

  // workaround: autoComplete="off" does not work in Chrome
  // https://github.com/mlaursen/react-md/issues/709
  componentDidMount() {
    // load .env
    this.setState({
      app: {
        title: CONFIG.title,
        subTitleChangePassword: CONFIG.subTitleChangePassword,
        logoRef: CONFIG.logoRef,
        logoLink: CONFIG.logoLink,
      },
    });
  }

  componentDidUpdate(prevProps) {
    const { alerts: prevAlerts, loading: prevLoading } = prevProps;
    const { alerts, loading, enqueueSnackbar, strings } = this.props;
    if (!isEqual(prevLoading, loading) && !isEqual(prevAlerts, alerts)) {
      showAlerts({ enqueueSnackbar, alerts, strings });
    }
  }

  handleSubmit = model => {
    const { actions, auth } = this.props;
    const isChanged = auth.get('isChanged');

    if (isChanged) {
      actions.logout();
      this.setState({ redirectLogin: true });
    } else {
      const pathname = window.location.pathname.split('/');
      const token = pathname[pathname.length - 1];

      actions.postChangePassword({ token, password: model.password });
    }
  };

  disableButton = () => this.setState({ canSubmit: false });

  enableButton = () => this.setState({ canSubmit: true });

  renderForm = () => {
    const { auth, strings, form, theme, loading } = this.props;
    const { canSubmit, password, password2, app = {} } = this.state;
    const isChanged = auth.get('isChanged');
    const isDisable = !canSubmit || loading;

    return (
      <div className="coog-form-login">
        <Header theme={theme} app={app} strings={strings} mode="change_password" />
        <Card className="md-block-centered card-block">
          <CardText>
            <Formsy
              key="basic-coog-block"
              ref="changePasswordForm" // eslint-disable-line react/no-string-refs
              onValidSubmit={this.handleSubmit}
              onValid={this.enableButton}
              onInvalid={this.disableButton}
              autoComplete="off"
            >
              {isChanged ? (
                <>
                  <div className="text">
                    <I18nMarkdown strings={strings} string="textPasswordChanged" />
                  </div>
                  <CardActions className="md-cell md-cell--12">
                    <Button raised={false} flat secondary swapTheming type="submit" className="coog-login-button">
                      {form.continueLabel}
                    </Button>
                  </CardActions>
                </>
              ) : (
                <>
                  <div className="text">
                    <I18nMarkdown strings={strings} string="textPasswordRules" />
                  </div>
                  <MyInput
                    id="password"
                    name="password"
                    label={form.passwordLabel}
                    placeholder={form.passwordPlaceholder}
                    validations={{
                      matchRegexp: /^(?=.*[A-Z].*[A-Z])(?=.*[!@#$&*])(?=.*[0-9].*[0-9])(?=.*[a-z].*[a-z].*[a-z]).{8,}$/,
                      /*
                      ^                         Start anchor
                      (?=.*[A-Z].*[A-Z])        Ensure string has 2 uppercase letters
                      (?=.*[!@#$&*])            Ensure string has 1 special case letter
                      (?=.*[0-9].*[0-9])        Ensure string has 2 digits
                      (?=.*[a-z].*[a-z].*[a-z]) Ensure string has 3 lowercase letters
                      .{8,}                     Ensure string is at least of length 8
                      $                         End anchor
                      */
                    }}
                    validationError="Mot de passe non valide"
                    required
                    type="password"
                    value={password}
                  />
                  <MyInput
                    id="password2"
                    name="password2"
                    label={form.passwordLabel2}
                    placeholder={form.passwordPlaceholder}
                    validations="equalsField:password"
                    validationError="Mot de passe non valide"
                    required
                    type="password"
                    value={password2}
                  />
                  <CardActions className="md-cell md-cell--12">
                    <Button
                      raised={isDisable}
                      disabled={isDisable}
                      flat={!isDisable}
                      secondary={!isDisable}
                      swapTheming={!isDisable}
                      type="submit"
                      className="coog-login-button"
                    >
                      {loading ? (
                        <div className="spinner">
                          <FontAwesomeIcon icon={faSpinner} size="lg" fixedWidth />
                        </div>
                      ) : (
                        form.submitLabel
                      )}
                    </Button>
                  </CardActions>
                </>
              )}
            </Formsy>
          </CardText>
        </Card>
        <Footer theme={theme} app={app} strings={strings} />
      </div>
    );
  };

  render() {
    const { redirectLogin } = this.state;

    if (redirectLogin) {
      return <Redirect to={{ pathname: '/' }} push />;
    }

    return this.renderForm();
  }
}

ChangePassword.propTypes = {
  enqueueSnackbar: PropTypes.func.isRequired, // eslint-disable-line
  auth: ImmutablePropTypes.contains({
    loading: PropTypes.bool.isRequired,
    isChanged: PropTypes.bool,
  }),
  actions: PropTypes.shape({
    logout: PropTypes.func.isRequired,
    postChangePassword: PropTypes.func.isRequired,
  }),
  form: PropTypes.shape({
    passwordLabel: PropTypes.string,
    passwordLabel2: PropTypes.string,
    passwordPlaceholder: PropTypes.string,
    passwordValidationError: PropTypes.string,
    continueLabel: PropTypes.string,
    submitLabel: PropTypes.string,
  }),
  strings: PropTypes.shape({
    pageTitle: PropTypes.string,
    canonical: PropTypes.string,
    textPasswordRules: PropTypes.string,
    textPasswordChanged: PropTypes.string,
  }),
  alerts: PropTypes.object, // eslint-disable-line
  loading: PropTypes.bool,
  theme: PropTypes.object, // eslint-disable-line
};

ChangePassword.defaultProps = {
  auth: { loading: false },
  strings: {
    pageTitle: 'Coog Portal - Changement de mot de passe',
    canonical: 'http://www.coopengo.com/',
    'link.connectToGoogle.label': 'Connexion avec Google',
    invalid_token: "Cette demande n'est pas valide ou a expirée.",
    token_expired: "Cette demande n'est pas valide ou a expirée.",
    bad_signature: "Cette demande n'est pas valide ou a expirée.",
    password_too_weak: 'Mot de passe trop simple.',
    textPasswordRules:
      "Saisissez un nouveau mot de passe.\n\nAssurez-vous d'inclure au moins\n- 8 caractères\n- 2 majuscules\n- 3 minuscules\n- 1 caractère spécial (! @ # $ & *)\n- 2 chiffres (0-9)",
    textPasswordChanged:
      'Votre changement de mot de passe a été effectué avec succès.\n\nVous pouvez vous identifier à nouveau et continuer votre navigation sur notre site.',
  },
  actions: {},
  form: {
    passwordLabel: 'Nouveau mot de passe',
    passwordLabel2: 'Confirmer le nouveau mot de passe',
    passwordPlaceholder: 'Mot de passe',
    passwordValidationError: 'Mot de passe incorrect',
    continueLabel: 'Continuer',
    submitLabel: 'Changer le mot de passe',
  },
  loading: false,
  theme: {},
};

const mapStateToProps = state => {
  const auth = state.get('auth');
  const theme = state.get('theme').toJS();

  return {
    auth,
    loading: get(auth.toJS(), 'loading', false),
    location: state.getIn(['router', 'location']),
    alerts: state.getIn(['auth', 'alerts']),
    theme,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ ...authActions, ...themeActions }, dispatch),
});

export default withSnackbar(connect(mapStateToProps, mapDispatchToProps)(ChangePassword));
